import type { z } from "zod";
import {
  CSRF_COOKIE,
  CSRF_HEADER,
  ApiError,
  ApiErrorResponse,
  ERROR_CODES,
  routes,
  type ApiErrorBody,
  type ErrorCode,
  type RouteDef,
  type RouteName,
  type Routes,
} from "@agentsaas/protocol";

/**
 * 带类型的 REST 客户端：路由、参数、响应类型全部从 protocol 的 `routes` 推导（doc/api_v1.md §0）。
 *   const c = createClient({ baseUrl: "/api/v1", auth: cookieAuth() });
 *   const msg = await c.call("agentInput", { params: { aid }, body: { parts } });
 */

type Simplify<T> = { [K in keyof T]: T[K] } & {};

type ParamsPart<R> = R extends { params: infer P extends z.ZodType } ? { params: z.input<P> } : unknown;
/** query 全部字段可选时，整个 query 也可省略 */
type QueryPart<R> = R extends { query: infer Q extends z.ZodType }
  ? object extends z.input<Q>
    ? { query?: z.input<Q> }
    : { query: z.input<Q> }
  : unknown;
type BodyPart<R> = R extends { body: infer B extends z.ZodType } ? { body: z.input<B> } : unknown;

export type CallInput<N extends RouteName> = Simplify<ParamsPart<Routes[N]> & QueryPart<Routes[N]> & BodyPart<Routes[N]>>;
export type CallOutput<N extends RouteName> = z.output<Routes[N]["response"]>;

export interface CallOptions {
  signal?: AbortSignal;
  /** 额外请求头（例如 fsWrite 的 If-Match 也可以用 ifMatch） */
  headers?: Record<string, string>;
  /** 乐观锁：对应 ETag */
  ifMatch?: string;
  /** 调用方自己管理重试时传入同一个 key；否则创建类请求自动生成 */
  idempotencyKey?: string;
}

type CallArgs<N extends RouteName> = object extends CallInput<N>
  ? [input?: CallInput<N>, options?: CallOptions]
  : [input: CallInput<N>, options?: CallOptions];

/** 客户端侧的错误：在 protocol 的 ApiError 上补充 requestId 与实际 HTTP 状态 */
export class ClientApiError extends ApiError {
  constructor(
    code: ErrorCode,
    params?: ApiErrorBody["params"],
    fields?: ApiErrorBody["fields"],
    readonly requestId?: string,
    readonly httpStatus?: number,
    /** 服务端返回 429 时的 Retry-After（秒） */
    readonly retryAfterSec?: number,
  ) {
    super(code, params, fields);
  }
}

/** 鉴权策略：Web 用 cookie（+CSRF），移动端 / 第三方用 bearer + refresh 轮换 */
export interface AuthProvider {
  /** 每次请求附加的头 */
  headers(): Record<string, string> | Promise<Record<string, string>>;
  /** fetch 的 credentials 设置 */
  credentials?: RequestCredentials;
  /** 收到 401 时调用一次；返回 true 表示刷新成功、可以重试 */
  refresh?(client: ApiClient): Promise<boolean>;
  /** 登录类接口返回 Session 后调用，用于保存 token */
  onSession?(session: CallOutput<"authLogin">): void;
  /** 刷新失败（彻底未登录）时调用 */
  onUnauthenticated?(): void;
}

export interface TokenStore {
  get(): { accessToken?: string; refreshToken?: string } | null;
  set(tokens: { accessToken?: string; refreshToken?: string } | null): void;
}

export function memoryTokenStore(initial: { accessToken?: string; refreshToken?: string } | null = null): TokenStore {
  let v = initial;
  return { get: () => v, set: (t) => void (v = t) };
}

/** bearer 模式：15 分钟 access + refresh 轮换 */
export function tokenAuth(store: TokenStore, opts: { onUnauthenticated?: () => void } = {}): AuthProvider {
  return {
    credentials: "omit",
    headers() {
      const at = store.get()?.accessToken;
      const h: Record<string, string> = {};
      if (at) h.Authorization = `Bearer ${at}`;
      return h;
    },
    async refresh(client) {
      const rt = store.get()?.refreshToken;
      if (!rt) return false;
      try {
        const s = await client.call("authRefresh", { body: { refreshToken: rt } }, undefined);
        store.set({ accessToken: s.accessToken, refreshToken: s.refreshToken });
        return true;
      } catch {
        store.set(null);
        return false;
      }
    },
    onSession(s) {
      if (s.accessToken || s.refreshToken) store.set({ accessToken: s.accessToken, refreshToken: s.refreshToken });
    },
    onUnauthenticated: opts.onUnauthenticated,
  };
}

/**
 * cookie 模式（Web）：HttpOnly session cookie + CSRF token。
 * 读 CSRF cookie 写入 CSRF 头（名字来自 protocol）；refresh 不带 refreshToken，服务端凭会话 cookie 续期。
 */
export function cookieAuth(
  opts: { csrfCookie?: string; csrfHeader?: string; getCookie?: () => string; onUnauthenticated?: () => void } = {},
): AuthProvider {
  const csrfCookie = opts.csrfCookie ?? CSRF_COOKIE;
  const csrfHeader = opts.csrfHeader ?? CSRF_HEADER;
  const getCookie = opts.getCookie ?? (() => (typeof document === "undefined" ? "" : document.cookie));
  return {
    credentials: "include",
    headers() {
      const m = new RegExp(`(?:^|; )${csrfCookie}=([^;]*)`).exec(getCookie());
      return m?.[1] ? { [csrfHeader]: decodeURIComponent(m[1]) } : {};
    },
    async refresh(client) {
      try {
        await client.call("authRefresh", { body: {} });
        return true;
      } catch {
        return false;
      }
    },
    onUnauthenticated: opts.onUnauthenticated,
  };
}

export interface ClientOptions {
  /** 例如 "/api/v1" 或 "https://host/api/v1" */
  baseUrl: string;
  auth?: AuthProvider;
  fetch?: typeof fetch;
  /** 每个请求附加的头（例如 Accept-Language） */
  headers?: () => Record<string, string>;
  /** 生成幂等键；默认 crypto.randomUUID */
  newIdempotencyKey?: () => string;
  /** 开发时用 zod 校验响应 */
  validateResponses?: boolean;
}

export interface ApiClient {
  call<N extends RouteName>(name: N, ...args: CallArgs<N>): Promise<CallOutput<N>>;
  /** 拼出某个路由的完整 URL（下载、调试用） */
  url<N extends RouteName>(name: N, input?: Partial<CallInput<N>>): string;
}

const SESSION_ROUTES = new Set<RouteName>(["authLogin", "authRegister", "authRefresh"]);

export function buildPath(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(/:([a-zA-Z]+)/g, (_, k: string) => {
    const v = params?.[k];
    if (v === undefined || v === null) throw new Error(`missing path param :${k}`);
    return encodeURIComponent(String(v));
  });
}

export function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) for (const x of v) sp.append(k, String(x));
    else sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

const STATUS_FALLBACK: Record<number, ErrorCode> = {
  400: "common.bad_request",
  401: "common.unauthenticated",
  403: "common.forbidden",
  404: "common.not_found",
  409: "common.conflict",
  412: "common.precondition_failed",
  429: "common.rate_limited",
  503: "common.unavailable",
};

/** 把任意响应转换为 ClientApiError；非标准响应按 HTTP 状态回退到通用错误码 */
export async function toApiError(res: Response): Promise<ClientApiError> {
  const retryAfter = Number(res.headers.get("Retry-After"));
  const ra = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined;
  let json: unknown;
  try {
    json = await res.json();
  } catch {
    json = undefined;
  }
  const parsed = ApiErrorResponse.safeParse(json);
  if (parsed.success) {
    const e = parsed.data.error;
    return new ClientApiError(e.code, e.params, e.fields, e.requestId, res.status, ra);
  }
  const code = STATUS_FALLBACK[res.status] ?? (res.status >= 500 ? "common.internal" : "common.bad_request");
  return new ClientApiError(code, undefined, undefined, undefined, res.status, ra);
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** 任意异常 → ApiError（网络错误视为 common.unavailable） */
export function normalizeError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  return new ClientApiError("common.unavailable");
}

const defaultKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export function createClient(opts: ClientOptions): ApiClient {
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = opts.baseUrl.replace(/\/$/, "");
  const newKey = opts.newIdempotencyKey ?? defaultKey;
  let refreshing: Promise<boolean> | null = null;

  const url = (name: RouteName, input: Record<string, unknown> = {}) => {
    const def: RouteDef = routes[name];
    return base + buildPath(def.path, input.params as Record<string, unknown>) + buildQuery(input.query as Record<string, unknown>);
  };

  const client: ApiClient = {
    url: (name, input) => url(name, input as Record<string, unknown>),
    async call(name, ...args) {
      const input = (args[0] ?? {}) as Record<string, unknown>;
      const options: CallOptions = args[1] ?? {};
      const def: RouteDef = routes[name];
      const idemKey = def.idempotent ? (options.idempotencyKey ?? newKey()) : undefined;
      const target = url(name, input);

      const send = async () => {
        const headers: Record<string, string> = {
          Accept: "application/json",
          ...opts.headers?.(),
          ...(await opts.auth?.headers()),
          ...options.headers,
        };
        if (def.body !== undefined) headers["Content-Type"] = "application/json";
        if (idemKey) headers["Idempotency-Key"] = idemKey;
        if (options.ifMatch) headers["If-Match"] = options.ifMatch;
        try {
          return await doFetch(target, {
            method: def.method,
            headers,
            body: def.body !== undefined ? JSON.stringify(input.body ?? {}) : undefined,
            credentials: opts.auth?.credentials ?? "same-origin",
            signal: options.signal,
          });
        } catch (e) {
          if ((e as Error)?.name === "AbortError") throw e;
          throw new ClientApiError("common.unavailable");
        }
      };

      let res = await send();
      // 401 → 刷新一次（并发请求共享同一次刷新）→ 重试；公开接口与刷新接口本身不重试
      if (res.status === 401 && def.auth === "user" && opts.auth?.refresh) {
        refreshing ??= opts.auth.refresh(client).finally(() => (refreshing = null));
        if (await refreshing) res = await send();
        if (res.status === 401) opts.auth.onUnauthenticated?.();
      }
      if (!res.ok) throw await toApiError(res);

      const text = await res.text();
      let data: unknown = text ? JSON.parse(text) : undefined;
      if (opts.validateResponses) {
        const r = def.response.safeParse(data);
        if (!r.success) throw new ClientApiError("common.internal", { route: name });
        data = r.data;
      }
      if (SESSION_ROUTES.has(name)) opts.auth?.onSession?.(data as CallOutput<"authLogin">);
      return data as CallOutput<typeof name>;
    },
  };
  return client;
}

/** HTTP 状态 → 错误码映射仍以 protocol 为准（供 mock / 测试复用） */
export const statusOf = (code: ErrorCode) => ERROR_CODES[code];
